import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import TickerIcon from "./TickerIcon";
import { TICKER_LIST } from "./tickerData";
import "./Ticker.css";

const MAX_SUGGESTIONS = 8;

/**
 * Comma-separated ticker input with suggestions for the token after the last
 * comma. onChange receives the raw string, same as the plain input it replaces.
 */
export default function TickerAutocomplete({ value, onChange, onKeyDown, className, ...rest }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const lastComma = value.lastIndexOf(",");
  const token = value.slice(lastComma + 1).trim().toUpperCase();

  const suggestions = useMemo(() => {
    if (!token) return [];
    const taken = new Set(value.slice(0, lastComma + 1).split(",").map((t) => t.trim().toUpperCase()));
    const byPrefix = [];
    const byName = [];
    for (const t of TICKER_LIST) {
      if (taken.has(t.s)) continue;
      if (t.s.startsWith(token)) byPrefix.push(t);
      else if (token.length >= 2 && t.n.toUpperCase().split(/[\s.,&-]+/).some((w) => w.startsWith(token))) byName.push(t);
    }
    byPrefix.sort((a, b) => a.s.length - b.s.length || a.s.localeCompare(b.s));
    return [...byPrefix, ...byName].slice(0, MAX_SUGGESTIONS);
  }, [token, value, lastComma]);

  const showList = open && suggestions.length > 0 && !(suggestions.length === 1 && suggestions[0].s === token);

  const select = (t) => {
    const head = value.slice(0, lastComma + 1);
    onChange(`${head}${head ? " " : ""}${t.s}, `);
    setActive(0);
  };

  const handleKeyDown = (e) => {
    if (showList) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        select(suggestions[Math.min(active, suggestions.length - 1)]);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div className="tkAuto">
      <input
        {...rest}
        className={className}
        type="text"
        autoComplete="off"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
      />
      {showList && (
        <ul className="tkAuto__list" role="listbox">
          {suggestions.map((t, i) => (
            <li
              key={t.s}
              role="option"
              aria-selected={i === active}
              className={`tkAuto__item${i === active ? " tkAuto__item--active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                select(t);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <TickerIcon symbol={t.s} size={20} />
              <span className="tkAuto__sym">{t.s}</span>
              <span className="tkAuto__name">{t.n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

TickerAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
};
